// Minimal markdown renderer: **bold**, *italic*, lists, paragraphs

function parseInline(text, prefix) {
  const result = [];
  const regex = /\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  let lastIndex = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) result.push(text.slice(lastIndex, m.index));
    if (m[1] !== undefined) result.push(<strong key={`${prefix}b${m.index}`}>{m[1]}</strong>);
    else result.push(<em key={`${prefix}i${m.index}`}>{m[2]}</em>);
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) result.push(text.slice(lastIndex));
  return result;
}

// During the typewriter animation a Markdown delimiter can arrive before its
// closing pair. Remove only the dangling marker so users never see raw ** or *
// flash on screen; complete formatting is still rendered normally.
export function sanitizeStreamingMarkdown(value) {
  let output = typeof value === "string" ? value : "";
  const boldMarkers = [...output.matchAll(/\*\*/g)];
  if (boldMarkers.length % 2 === 1) {
    const index = boldMarkers[boldMarkers.length - 1].index;
    output = `${output.slice(0, index)}${output.slice(index + 2)}`;
  }

  const italicMarkers = [...output.matchAll(/(?<!\*)\*(?!\*)/g)];
  if (italicMarkers.length % 2 === 1) {
    const index = italicMarkers[italicMarkers.length - 1].index;
    output = `${output.slice(0, index)}${output.slice(index + 1)}`;
  }

  return output;
}

function sequentialOrderedItems(matches, text) {
  if (matches.length < 2 || Number(matches[0][1]) !== 1) return null;

  for (let index = 0; index < matches.length; index += 1) {
    if (Number(matches[index][1]) !== index + 1) return null;
  }

  const items = matches.map((match, index) => {
    const start = match.index + match[0].length;
    const end = index + 1 < matches.length ? matches[index + 1].index : text.length;
    return text.slice(start, end).trim();
  });

  return items.every(Boolean) ? items : null;
}

// Models sometimes emit short choices as `1. First 2. Second` rather than
// putting each item on its own line. Treat that as a list only when it starts
// at 1 and contains at least two strictly sequential markers. This keeps
// decimals, versions, years, and incomplete streaming output as normal prose.
function parseInlineOrderedList(text) {
  const matches = [...text.matchAll(/(?:^|\s)(\d+)\.\s+/g)];
  const items = sequentialOrderedItems(matches, text);
  if (!items) return null;

  const intro = text.slice(0, matches[0].index).trim();
  // A same-line introduction is accepted only when punctuation clearly opens
  // a list; this avoids turning ordinary prose containing “1.” and “2.” into one.
  if (intro && !/[:?]$/.test(intro)) return null;
  return { intro, items };
}

function parseOrderedBlock(text) {
  const lines = text.split("\n").map(line => line.trim());
  const firstListLine = lines.findIndex(line => /^1\.\s+/.test(line));

  if (firstListLine >= 0) {
    const listLines = lines.slice(firstListLine);
    const matches = listLines.map(line => line.match(/^(\d+)\.\s+(.+)$/));
    const isSequential = matches.every((match, index) => (
      match && Number(match[1]) === index + 1
    ));

    if (matches.length >= 2 && isSequential) {
      return {
        intro: lines.slice(0, firstListLine).join(" ").trim(),
        items: matches.map(match => match[2].trim()),
      };
    }
  }

  return parseInlineOrderedList(text.trim());
}

// Block renderer — replaces a <p> or <div> wrapper
export function Md({ children, className }) {
  if (!children) return null;

  const paras = children.split(/\n\n+/);
  const nodes = [];

  for (let pi = 0; pi < paras.length; pi++) {
    const para = paras[pi].trim();
    if (!para) continue;
    const lines = para.split('\n');
    const isList = lines.every(l => /^[-*] /.test(l.trim()));
    const orderedBlock = parseOrderedBlock(para);
    if (orderedBlock) {
      if (orderedBlock.intro) {
        nodes.push(
          <p key={`${pi}-intro`}>{parseInline(orderedBlock.intro, `${pi}-intro-`)}</p>
        );
      }
      nodes.push(
        <ol key={`${pi}-list`}>
          {orderedBlock.items.map((item, li) => (
            <li key={li}>{parseInline(item, `${pi}-${li}-`)}</li>
          ))}
        </ol>
      );
    } else if (isList) {
      nodes.push(
        <ul key={pi}>
          {lines.map((l, li) => (
            <li key={li}>{parseInline(l.trim().slice(2), `${pi}-${li}-`)}</li>
          ))}
        </ul>
      );
    } else {
      nodes.push(
        <p key={pi}>{parseInline(para.replace(/\n/g, ' '), `${pi}-`)}</p>
      );
    }
  }

  if (nodes.length === 0) return null;
  if (nodes.length === 1 && nodes[0].type === 'p') {
    return <p className={className}>{nodes[0].props.children}</p>;
  }
  return <div className={className}>{nodes}</div>;
}

// Inline renderer — goes inside an existing <p> or heading tag
export function MdInline({ children }) {
  if (!children) return null;
  return <>{parseInline(children, 'inline-')}</>;
}
